/* eslint-disable max-len */
/* eslint-disable no-sequences */
import React from 'react';
import classnames from 'classnames';
import { CapSelector, JoinSelector, LineStyleSelector } from '@leverege/molten/data-viewers';
import { useValueChange } from '@leverege/ui-hooks';
import { ModelTypeSelector } from '@leverege/ui-plugin';
import { Colorizers } from '@leverege/ui-attributes';
import { TabPane, NumericInput, TextInput, Checkbox, Popup, Text, PropertyGrid } from '@leverege/ui-elements';
import I18N from '@leverege/i18n';
import Model from "./FilterableZoneGeoShapeModel.js";
import S from './Grid.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.dataViewers.map.GeoShapeEditor');
function GeoShapeEditor(props) {
  const {
    value
  } = props;
  const onModelChange = useValueChange(Model, props);
  if (value == null) {
    return null;
  }
  const objectType = Model.getObjectType(value);
  return /*#__PURE__*/_jsxs(TabPane, {
    children: [/*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('visibility'),
      tabId: "vis",
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          children: /*#__PURE__*/_jsx(Checkbox, {
            label: t('visible'),
            eventData: "setVisible",
            value: Model.getVisible(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('showZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            eventData: "setShowAtZoom",
            min: 0,
            max: Math.min(25, Model.getHideAtZoom(value)),
            allowError: true,
            value: Model.getShowAtZoom(value),
            onChange: onModelChange
          }, `haz-${Model.getHideAtZoom(value) || 'n'}`)
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('hideZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: Math.max(0, Model.getShowAtZoom(value)),
            max: 25,
            eventData: "setHideAtZoom",
            allowError: true,
            value: Model.getHideAtZoom(value),
            onChange: onModelChange
          }, `saz-${Model.getShowAtZoom(value) || 'n'}`)
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          children: /*#__PURE__*/_jsx(Popup, {
            title: t('help'),
            icon: tIcon('helpIcon', 'fa fa-question-circle fa-fw'),
            children: /*#__PURE__*/_jsxs("div", {
              className: S.pad,
              children: [/*#__PURE__*/_jsx(Text, {
                className: classnames(S.fill, S.hint),
                variant: "caption",
                children: t('settingZoomDescription')
              }), /*#__PURE__*/_jsx(Text, {
                className: S.fill,
                variant: "subtitle",
                children: t('zoomLevel')
              }), /*#__PURE__*/_jsxs("div", {
                className: S.grid,
                style: {
                  columnGap: 'var( --size-padXLarge, 32px)'
                },
                children: [/*#__PURE__*/_jsx("span", {
                  children: "0"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('0Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "3"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('3Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "4"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('4Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "6"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('6Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "10"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('10Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "15"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('15Zoom')
                }), /*#__PURE__*/_jsx("span", {
                  children: "18"
                }), /*#__PURE__*/_jsx("span", {
                  children: t('18Zoom')
                })]
              })]
            })
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Subheader, {
          title: t('interaction')
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          children: /*#__PURE__*/_jsx(Checkbox, {
            label: t('rollover'),
            eventData: "setRolloverable",
            value: Model.getRolloverable(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          children: /*#__PURE__*/_jsx(Checkbox, {
            label: t('selectable'),
            eventData: "setSelectable",
            value: Model.getSelectable(value),
            onChange: onModelChange
          })
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('appearance'),
      tabId: "app",
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        children: [/*#__PURE__*/_jsx(PropertyGrid.Subheader, {
          title: t('fill')
        }), ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            ...props,
            objectType
          },
          modelKey: 'colorizer',
          isPropertyGrid: true,
          values: Colorizers.getColorizersFor(objectType),
          value: Model.getFillColor(value),
          eventData: 'setFillColor',
          label: t('color'),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('opacity'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0,
            max: 1,
            float: true,
            eventData: "setFillOpacity",
            allowError: true,
            value: Model.getFillOpacity(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Subheader, {
          title: t('outline')
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('width'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0,
            max: 50,
            float: true,
            eventData: "setLineWidth",
            allowError: true,
            value: Model.getLineWidth(value),
            onChange: onModelChange
          })
        }), ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            ...props,
            objectType
          },
          modelKey: 'colorizer',
          isPropertyGrid: true,
          values: Colorizers.getColorizersFor(objectType),
          value: Model.getLineColor(value),
          eventData: 'setLineColor',
          label: t('color'),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('opacity'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0,
            max: 1,
            float: true,
            eventData: "setLineOpacity",
            allowError: true,
            value: Model.getLineOpacity(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('style'),
          children: /*#__PURE__*/_jsx(LineStyleSelector, {
            allowCustom: false,
            value: Model.getLineStyle(value),
            eventData: "setLineStyle",
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('join'),
          children: /*#__PURE__*/_jsx(JoinSelector, {
            allowBevel: false,
            eventData: "setLineJoin",
            value: Model.getLineJoin(value) || 'miter',
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('cap'),
          children: /*#__PURE__*/_jsx(CapSelector, {
            allowButt: false,
            eventData: "setLineCap",
            value: Model.getLineCap(value) || 'square',
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Subheader, {
          title: t('label')
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          children: /*#__PURE__*/_jsx(Checkbox, {
            label: t('visible'),
            value: Model.getLabelVisible(value),
            onChange: onModelChange,
            eventData: "setLabelVisible"
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('attribute'),
          children: /*#__PURE__*/_jsx(TextInput, {
            size: 24,
            eventData: "setLabelAttribute",
            allowError: true,
            value: Model.getLabelAttribute(value),
            onChange: onModelChange
          })
        }), ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            ...props,
            objectType
          },
          modelKey: 'colorizer',
          isPropertyGrid: true,
          label: t('color'),
          values: Colorizers.getColorizersFor(objectType),
          value: Model.getLabelColor(value),
          eventData: 'setLabelColor',
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('size'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0,
            max: 50,
            float: true,
            eventData: "setLabelSize",
            allowError: true,
            value: Model.getLabelSize(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('opacity'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            min: 0,
            max: 1,
            float: true,
            eventData: "setLabelOpacity",
            allowError: true,
            value: Model.getLabelOpacity(value),
            onChange: onModelChange
          })
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('rollover'),
      tabId: "rollover",
      children: conditionAppearance('Rollover', onModelChange, value, objectType, props)
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('selected'),
      tabId: "selected",
      children: conditionAppearance('Selected', onModelChange, value, objectType, props)
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('filters'),
      tabId: "filters",
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        children: [/*#__PURE__*/_jsx(PropertyGrid.Subheader, {
          title: t('customFilters')
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          children: /*#__PURE__*/_jsx(Checkbox, {
            containerClassName: S.fill,
            label: t('includeOutdoorZones'),
            title: t('includeOutdoorZonesDescription'),
            value: Model.getIncludeOutdoorZones(value),
            onChange: onModelChange,
            eventData: "setIncludeOutdoorZones"
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          children: /*#__PURE__*/_jsx(Checkbox, {
            containerClassName: S.fill,
            label: t('includeIndoorZonesRPSEnabled'),
            title: t('includeIndoorZonesRPSEnabledDescription'),
            value: Model.getIncludeIndoorZonesRPSEnabled(value),
            onChange: onModelChange,
            eventData: "setIncludeIndoorZonesRPSEnabled"
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          children: /*#__PURE__*/_jsx(Checkbox, {
            containerClassName: S.fill,
            label: t('includeIndoorZonesRPSDisabled'),
            title: t('includeIndoorZonesRPSDisabledDescription'),
            value: Model.getIncludeIndoorZonesRPSDisabled(value),
            onChange: onModelChange,
            eventData: "setIncludeIndoorZonesRPSDisabled"
          })
        })]
      })
    })]
  });
}
function conditionAppearance(prefix, onModelChange, value, objectType, props) {
  const omc = onModelChange;
  const shfc = `setHas${prefix}FillColor`;
  const shfo = `setHas${prefix}FillOpacity`;
  const shlw = `setHas${prefix}LineWidth`;
  const shls = `setHas${prefix}LineStyle`;
  const shlc = `setHas${prefix}LineColor`;
  const shlo = `setHas${prefix}LineOpacity`;
  const sfc = `set${prefix}FillColor`;
  const sfo = `set${prefix}FillOpacity`;
  const slw = `set${prefix}LineWidth`;
  const slc = `set${prefix}LineColor`;
  const sls = `set${prefix}LineStyle`;
  const slo = `set${prefix}LineOpacity`;
  const gfc = Model[`get${prefix}FillColor`](value);
  const gfo = Model[`get${prefix}FillOpacity`](value);
  const gls = Model[`get${prefix}LineStyle`](value);
  const glw = Model[`get${prefix}LineWidth`](value);
  const glc = Model[`get${prefix}LineColor`](value);
  const glo = Model[`get${prefix}LineOpacity`](value);
  const hfc = Model[`getHas${prefix}FillColor`](value);
  const hfo = Model[`getHas${prefix}FillOpacity`](value);
  const hlw = Model[`getHas${prefix}LineWidth`](value);
  const hls = Model[`getHas${prefix}LineStyle`](value);
  const hlc = Model[`getHas${prefix}LineColor`](value);
  const hlo = Model[`getHas${prefix}LineOpacity`](value);
  return /*#__PURE__*/_jsxs(PropertyGrid, {
    children: [/*#__PURE__*/_jsx(PropertyGrid.Subheader, {
      title: t('fill')
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fillCell: true,
      children: /*#__PURE__*/_jsx(Checkbox, {
        label: t('color'),
        value: hfc,
        onChange: omc,
        eventData: shfc
      })
    }), !hfc ? null : ModelTypeSelector.createSelectorAndEditor({
      editorProps: {
        ...props,
        objectType
      },
      modelKey: 'colorizer',
      isPropertyGrid: true,
      values: Colorizers.getColorizersFor(objectType),
      value: gfc,
      eventData: sfc,
      onChange: omc
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: /*#__PURE__*/_jsx(Checkbox, {
        label: t('opacity'),
        value: hfo,
        onChange: omc,
        eventData: shfo
      }),
      children: hfo && /*#__PURE__*/_jsx(NumericInput, {
        min: 0,
        max: 1,
        float: true,
        eventData: sfo,
        allowError: true,
        value: gfo,
        onChange: omc
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Subheader, {
      title: t('outline')
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: /*#__PURE__*/_jsx(Checkbox, {
        label: "Width",
        value: hlw,
        onChange: omc,
        eventData: shlw
      }),
      children: hlw && /*#__PURE__*/_jsx(NumericInput, {
        min: 0,
        max: 50,
        float: true,
        eventData: slw,
        allowError: true,
        value: glw,
        onChange: omc
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fillCell: true,
      children: /*#__PURE__*/_jsx(Checkbox, {
        label: t('color'),
        value: hlc,
        onChange: omc,
        eventData: shlc
      })
    }), hlc && ModelTypeSelector.createSelectorAndEditor({
      editorProps: {
        ...props,
        objectType
      },
      modelKey: 'colorizer',
      values: Colorizers.getColorizersFor(objectType),
      value: glc,
      isPropertyGrid: true,
      eventData: slc,
      onChange: omc
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: /*#__PURE__*/_jsx(Checkbox, {
        label: t('opacity'),
        value: hlo,
        onChange: omc,
        eventData: shlo
      }),
      children: hlo && /*#__PURE__*/_jsx(NumericInput, {
        min: 0,
        max: 1,
        float: true,
        eventData: slo,
        allowError: true,
        value: glo,
        onChange: omc
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: /*#__PURE__*/_jsx(Checkbox, {
        label: t('style'),
        value: hls,
        onChange: omc,
        eventData: shls
      }),
      children: hls && /*#__PURE__*/_jsx(LineStyleSelector, {
        allowCustom: false,
        value: gls,
        eventData: sls,
        onChange: omc
      })
    })]
  });
}
export default /*#__PURE__*/React.memo(GeoShapeEditor);